import { ReactNode, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';

import { AnimationProps } from 'framer-motion';
import styled from 'styled-components';

import { useUnsavedChangesModal } from '@components/generic-modals';
import { RouterTabsHeader } from '@components/header/router-tabs-header';
import { TabsHeader } from '@components/header/tabs-header';
import { IconButton } from '@components/icon-button';
import { IconArrowLeft } from '@components/icons/arrow-left';
import { IconCloseRegular } from '@components/icons/close-regular';
import { IconEdit } from '@components/icons/edit';
import { IconForm } from '@components/icons/form';
import { IconSend } from '@components/icons/send';
import { LabelInputText } from '@components/label-input-text';
import { PageContainer } from '@components/page-containers';
import { hideSidebar } from '@components/sidebar';
import { Stack } from '@components/stack';
import { Text } from '@components/text/text';
import { Keys } from '@components/types/keyboard';
import { useKeyPress } from '@hooks/use-keypress';
import { useNavigationBlock } from '@hooks/use-navigation-block';
import { useScopedTranslate } from '@locale/translate';
import { lineClamp } from '@styles/layout-helpers';
import { pxToRem } from '@utils/converters';

import { FormBuilderContent, FormBuilderContentProps } from './content';
import { FormBuilderContextProvider, FormBuilderContextType } from './context';
import { createBuilderStore } from './create-builder-store';
import { useEditFormDetailsSidebar } from './edit-form-details';
import { FormBuilderProps } from './form-builder';

const useFormBuilderShared = (props: FormBuilderProps) => {
  const {
    onSave,
    form,
    submissionMessage,
    name: templateName,
    description: templateDescription,
    predefinedOptionsLists = [],
    extraTabs = [],
    extraTopActions,
    extraFieldGroups,
    secondaryActions,
    isCreating = false,
    formVariant = 'input',
    mode = 'edit',
    variableGroups = [],
    ...rest
  } = props;

  const formStoreRef = useRef(
    createBuilderStore({
      formType: 'form',
      form,
      formVariant: formVariant,
      mode,
      predefinedOptionsLists,
      ...rest,
    })
  );
  const submissionStoreRef = useRef(
    createBuilderStore({
      formType: 'submission',
      form: submissionMessage,
      formVariant: formVariant,
      mode,
      predefinedOptionsLists,
      ...rest,
    })
  );

  const { t } = useScopedTranslate(locale);
  const [name, setName] = useState(templateName ?? 'Untitled form');
  const [description, setDescription] = useState(templateDescription ?? '');

  const isFormStoreEditing = formStoreRef.current.useIsEditMode();
  const isSubmissionStoreEditing = submissionStoreRef.current.useIsEditMode();
  const isFormDirty = formStoreRef.current.useIsDirty();
  const isSubmissionDirty = submissionStoreRef.current.useIsDirty();
  const isDirty = isFormDirty || isSubmissionDirty;
  const isFormSaving = formStoreRef.current.useIsSaving();
  const isSubmissionSaving = submissionStoreRef.current.useIsSaving();
  const isSaving = isFormSaving || isSubmissionSaving;
  const showEditSidebar = useEditFormDetailsSidebar();

  useEffect(() => {
    if (templateName) {
      setName(templateName);
    }
  }, [templateName]);

  const onSaveDetails = (newName: string, newDescription?: string) => {
    if (isCreating) {
      return createBaseSave({ newName, newDescription });
    } else {
      return onSave({ name: newName, description: newDescription });
    }
  };

  const createBaseSave = async ({
    newName,
    newDescription,
    path,
  }: {
    newName?: string;
    newDescription?: string;
    path?: string;
  } = {}) => {
    if (formStoreRef.current.getState().isSaving) {
      return;
    }

    formStoreRef.current.actions.startSaving();
    submissionStoreRef.current.actions.startSaving();

    const createNavigationString = path ? path.split('/').pop() : undefined;

    try {
      await onSave(
        {
          fieldsConfig: {
            fields: formStoreRef.current.actions.deserialize(),
            schema: 'v2',
          },
          name: newName ?? name,
          description: newDescription ?? description,
          submissionMessageConfig: {
            fields: submissionStoreRef.current.actions.deserialize(),
            schema: 'v2',
          },
          removedImages: [
            ...formStoreRef.current.getState().removedImages,
            ...submissionStoreRef.current.getState().removedImages,
          ],
          removedFiles: [
            ...formStoreRef.current.getState().removedFiles,
            ...submissionStoreRef.current.getState().removedFiles,
          ],
        },
        (overrideForm, overrideSubmissionMessage) => {
          formStoreRef.current.actions.savePoint(overrideForm);
          submissionStoreRef.current.actions.savePoint(overrideSubmissionMessage);
        },
        createNavigationString
      );
    } finally {
      formStoreRef.current.actions.stopSaving();
      submissionStoreRef.current.actions.stopSaving();
    }
  };

  const resetStores = () => {
    formStoreRef.current.actions.resetState();
    submissionStoreRef.current.actions.resetState();
  };

  const createTabs = (
    cancelButtonProps: { href?: string; onClick?: () => void },
    onSave?: () => void
  ) => [
    {
      label: t('form'),
      section: 'form',
      dataTestId: 'formbuilder-form',
      content: (
        <FormBuilderInstance
          onSave={async () => {
            await createBaseSave();
            onSave?.();
          }}
          cancelButtonProps={cancelButtonProps}
          contextValue={{
            store: formStoreRef.current,
            predefinedOptionsLists,
            variableGroups: variableGroups,
          }}
          extraFieldGroups={extraFieldGroups}
        />
      ),
      icon: <IconForm />,
    },
    {
      label: t('submission'),
      section: 'submission',
      dataTestId: 'formbuilder-submission',
      content: (
        <FormBuilderInstance
          onSave={async () => {
            await createBaseSave();
            onSave?.();
          }}
          cancelButtonProps={cancelButtonProps}
          contextValue={{
            store: submissionStoreRef.current,
            predefinedOptionsLists,
            variableGroups: variableGroups,
          }}
        />
      ),
      icon: <IconSend />,
    },
  ];

  const createTopActions = (
    additionalActions?: ReactNode,
    showSecondaryActions: boolean = isEditing
  ) => (
    <TopActions>
      <Stack space={4}>
        <Stack space={6} orientation="horizontal" align="flex-start" data-testid="top-buttons">
          <div id="viewport-portal" />
          <div id="draft-portal" />
          <Stack space={2} orientation="horizontal" align="center">
            <div id="cancel-save-portal" />
            <div id="preview-portal" />
            {extraTopActions}
            {additionalActions}
          </Stack>
        </Stack>
        {secondaryActions && showSecondaryActions && (
          <Stack orientation="horizontal" justify="flex-end" align="center">
            {secondaryActions}
          </Stack>
        )}
      </Stack>
    </TopActions>
  );

  const createCommonHeaderProps = (
    showEditDetails?: boolean,
    isEditing: boolean = isFormStoreEditing && isSubmissionStoreEditing
  ) => ({
    title: (
      <TitleContainer>
        {isEditing ? (
          <LabelInputText
            key={name}
            defaultValue={name}
            font="bold18"
            maxLength={255}
            onSave={(value) => {
              if (value !== name) {
                onSaveDetails(value);
              }
            }}
            label={t('formTitle')}
            hideLabel
            dataTestid="form-title"
            dataCy="formbuilder-title-menu"
            extraMenuItems={
              showEditDetails
                ? [
                    {
                      label: t('editDetails'),
                      icon: <IconEdit />,
                      onSelect: () => {
                        showEditSidebar(
                          {
                            name,
                            description,
                            onSave: async ({ name, description }) => {
                              await onSaveDetails(name, description);
                              setName(name);
                              setDescription(description);
                              hideSidebar();
                            },
                          },
                          {
                            hasBackdrop: true,
                          }
                        );
                      },
                    },
                  ]
                : []
            }
          />
        ) : (
          <Stack space={2} orientation="horizontal" align="center" justify="center">
            <div id="backarrow-portal" />
            <NameContainer>
              <Text text={name} font="bold18" dataTestid="form-title" tooltip={name} />
            </NameContainer>
          </Stack>
        )}
      </TitleContainer>
    ),
    actions: createTopActions(),
    hideTabs: !isEditing,
  });

  const isEditing = isFormStoreEditing && isSubmissionStoreEditing;

  return {
    name,
    setName,
    description,
    setDescription,
    isEditing,
    isFormStoreEditing,
    isSubmissionStoreEditing,
    isDirty,
    isSaving,
    isCreating,
    t,
    extraTabs,
    isFormDirty,
    isSubmissionDirty,

    createBaseSave,
    onSaveDetails,
    resetStores,
    createTabs,
    createTopActions,
    createCommonHeaderProps,
  };
};

export const FormBuilderTabsTop = (props: FormBuilderProps) => {
  const shared = useFormBuilderShared(props);
  const { onComplete } = props;
  const showUnsavedChangesModal = useUnsavedChangesModal();
  const [currentTabId, setCurrentTabId] = useState('form');

  const handleCloseModal = () => {
    shared.resetStores();
    onComplete?.();
  };

  const handleExit = () => {
    if (shared.isDirty) {
      showUnsavedChangesModal({
        title: shared.isCreating ? shared.t('newFormTitle') : shared.t('unsavedChangesTitle'),
        description: shared.isCreating
          ? shared.t('newFormDescription')
          : shared.t('unsavedChangesDescription'),
        saveText: shared.t('saveForm'),
        onSave: async () => {
          await shared.createBaseSave();
          onComplete?.();
        },
        onDiscard: () => {
          shared.resetStores();
          onComplete?.();
        },
      });
    } else {
      onComplete?.();
    }
  };

  useKeyPress({
    key: Keys.Escape,
    enabled: Boolean(onComplete),
    handler: handleExit,
  });

  const handleTabChange = (newTabId: string) => {
    if (currentTabId === newTabId) {
      return;
    }

    const currentTabHasDirtyChanges =
      (currentTabId === 'form' && shared.isFormDirty) ||
      (currentTabId === 'submission' && shared.isSubmissionDirty);

    if (currentTabHasDirtyChanges) {
      showUnsavedChangesModal({
        title: shared.t('unsavedChangesTitle'),
        description: shared.t('unsavedChangesDescription'),
        saveText: shared.t('saveForm'),
        onSave: async () => {
          await shared.createBaseSave();
          setCurrentTabId(newTabId);
        },
        onDiscard: () => {
          shared.resetStores();
          setCurrentTabId(newTabId);
        },
      });
    } else {
      setCurrentTabId(newTabId);
    }
  };

  const tabs = shared.createTabs({ onClick: handleCloseModal });

  const tabsForTabsHeader = [
    ...tabs.map((tab) => ({
      id: tab.section,
      label: tab.label,
      content: tab.content,
      icon: tab.icon,
      dataTestId: tab.dataTestId,
    })),
    ...shared.extraTabs.map((tab) => ({
      id: tab.section,
      label: tab.label,
      content: tab.content,
      icon: tab.icon,
      dataTestId: tab.dataTestId,
      counter: tab.counter,
    })),
  ];

  // Use the current tab's editing state for the header
  const isEditing =
    currentTabId === 'form'
      ? shared.isFormStoreEditing
      : currentTabId === 'submission'
      ? shared.isSubmissionStoreEditing
      : shared.isFormStoreEditing && shared.isSubmissionStoreEditing;

  const commonHeaderProps = shared.createCommonHeaderProps(false, isEditing);

  return (
    <TabsHeader
      tabs={tabsForTabsHeader}
      activeTab={currentTabId}
      onTabChange={handleTabChange}
      {...commonHeaderProps}
      actions={shared.createTopActions(
        <IconButton icon={<IconCloseRegular />} onClick={handleExit} />,
        isEditing
      )}
    />
  );
};

export const FormBuilderRouterTop = (props: FormBuilderProps) => {
  const shared = useFormBuilderShared(props);
  const { backUrl } = props;

  useNavigationBlock({
    shouldBlock: shared.isDirty,
    onSave: (path) => shared.createBaseSave({ path }),
    onDiscard: () => shared.resetStores(),
    title: shared.isCreating ? shared.t('newFormTitle') : shared.t('unsavedChangesTitle'),
    description: shared.isCreating
      ? shared.t('newFormDescription')
      : shared.t('unsavedChangesDescription'),
    saveText: shared.t('saveForm'),
  });

  const tabs = shared.createTabs({ href: backUrl });
  const tabsForRouterTabsHeader = [...tabs, ...shared.extraTabs];
  const commonHeaderProps = shared.createCommonHeaderProps(true);

  return (
    <RouterTabsHeader
      tabs={tabsForRouterTabsHeader}
      {...commonHeaderProps}
      backAction={
        shared.isEditing && backUrl ? (
          <IconButton
            disabled={shared.isSaving}
            data-cy="formbuilder-back"
            icon={<IconArrowLeft />}
            variant="ghost-purple"
            href={backUrl}
          />
        ) : undefined
      }
    />
  );
};

type FormBuilderInstanceProps = {
  contextValue: FormBuilderContextType;
  extraFieldGroups?: ReactNode;
  cancelButtonProps?: {
    href?: string;
    onClick?: () => void;
  };
} & FormBuilderContentProps;

const FormBuilderInstance = (props: FormBuilderInstanceProps) => {
  const { onSave, contextValue, extraFieldGroups, cancelButtonProps } = props;
  return (
    <FormBuilderContextProvider value={contextValue}>
      <PageContainer.Page>
        <FormBuilderContent
          onSave={onSave}
          extraFieldGroups={extraFieldGroups}
          cancelButtonProps={cancelButtonProps}
        />
      </PageContainer.Page>
    </FormBuilderContextProvider>
  );
};

export type FormBuilderPortalProps = {
  portalId: string;
  children: React.ReactNode;
};

export const FormBuilderPortal = ({ portalId, children }: FormBuilderPortalProps) => {
  const [portalElement, setPortalElement] = useState<HTMLElement | null>(null);

  useEffect(() => {
    setPortalElement(document.getElementById(portalId));
  }, [portalId]);

  return portalElement ? ReactDOM.createPortal(children, portalElement) : null;
};

export const animations = {
  back: {
    initial: { x: -10, width: 0, overflow: 'hidden', opacity: 0 },
    animate: { x: 0, width: 40, opacity: 1 },
    exit: { x: -10, width: 0, opacity: 0, overflow: 'hidden', marginRight: '-0.5rem' },
    transition: { type: 'just' },
  },
  cancel: {
    initial: { width: 0, opacity: 0, overflow: 'hidden' },
    animate: { width: 'auto', opacity: 1, overflow: 'visible' },
    exit: { width: 0, opacity: 0, overflow: 'hidden' },
    transition: { duration: 0.15 },
  },
  draft: {
    initial: { display: 'none', opacity: 0 },
    animate: { display: 'block', opacity: 1 },
    transition: { delay: 0.3, duration: 0.3 },
  },
  viewport: {
    initial: { opacity: 0, x: 30 },
    animate: { opacity: 1, x: 0, transition: { type: 'just' } },
    exit: { opacity: 0, x: 30, transition: { duration: 0.2 } },
  },
} satisfies Record<string, AnimationProps>;

const TopActions = styled(Stack).attrs({
  space: 2,
  justify: 'space-between',
  align: 'flex-end',
})({
  paddingBottom: pxToRem(16),
});

const TitleContainer = styled.div({
  paddingRight: pxToRem(48),
});

const NameContainer = styled.div({
  ...lineClamp('-webkit-box', 2, true),
});

const locale = {
  en: {
    rename: 'Rename',
    form: 'Form',
    submission: 'Submission',
    journeys: 'Journeys',
    newFormTitle: 'Save form?',
    unsavedChangesTitle: 'Save changes?',
    unsavedChangesDescription:
      'This form has unsaved changes.\nWould you like to save or discard them?',
    newFormDescription: 'This form has not been saved\nwould you like to save or discard it?',
    saveForm: 'Save form',
    formTitle: 'Form title',
    travellers: 'Travellers',
    actions: 'Actions',
    noJourneys: 'No journeys',
    noJourneysDescription: 'This form is currently not used in any journeys',
    editDetails: 'Edit form details',
  },
};
